import { BaseError } from "@/domains/errors/kinds/BaseError";
import { ErrorOptions } from "@/domains/errors/types";
import { JsonObject } from "@/domains/common/types";

export enum AudioRecordingErrorSource {
  AssemblyAI = "assembly_ai",
  RecallDesktop = "recall_desktop",
  WebMediaRecorder = "web_media_recorder",
}

export enum AudioRecordingErrorStage {
  TokenFetch = "token_fetch",
  Start = "start",
  Stop = "stop",
  Pause = "pause",
  Resume = "resume",
  PayloadParse = "payload_parse",
  WebsocketConnect = "websocket_connect",
  WebsocketError = "websocket_error",
  WebsocketSend = "websocket_send",
  Reconnect = "reconnect",
  AudioContextResume = "audio_context_resume",
  AudioWorklet = "audio_worklet",
  AudioProcessing = "audio_processing",
  PrepareWindow = "prepare_window",
  UploadCredentials = "upload_credentials",
  RecorderEvent = "recorder_event",
  TranscriberEvent = "transcriber_event",
  TranscriptMap = "transcript_map",
  CaptureStart = "capture_start",
  CaptureStop = "capture_stop",
  CapturePause = "capture_pause",
  CaptureResume = "capture_resume",
  EmitChunk = "emit_chunk",
  Event = "event",
}

type SummaryValue = string | number | boolean;

export interface AudioRecordingErrorOptions extends ErrorOptions {
  source: AudioRecordingErrorSource;
  stage?: AudioRecordingErrorStage;
  summary?: string;
  details?: Record<string, SummaryValue | null | undefined>;
}

const DEFAULT_MESSAGE = "Audio recording error";
const MAX_SUMMARY_PAIRS = 8;
const MAX_VALUE_LENGTH = 120;

const buildLabel = (source: AudioRecordingErrorSource, stage?: AudioRecordingErrorStage): string =>
  stage ? `${source}/${stage}` : source;

const truncateValue = (value: string): string => {
  if (value.length <= MAX_VALUE_LENGTH) {
    return value;
  }
  return `${value.slice(0, MAX_VALUE_LENGTH - 3)}...`;
};

const buildSummary = (
  details?: Record<string, SummaryValue | null | undefined>
): string | undefined => {
  if (!details) return undefined;
  const entries = Object.entries(details)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => {
      const rendered = truncateValue(String(value));
      return `${key}=${rendered}`;
    })
    .slice(0, MAX_SUMMARY_PAIRS);

  if (!entries.length) {
    return undefined;
  }
  return entries.join("; ");
};

const normalizeDetails = (
  details?: Record<string, SummaryValue | null | undefined>
): JsonObject | undefined => {
  if (!details) return undefined;
  const entries = Object.entries(details).filter(([, value]) => value !== undefined);
  if (!entries.length) return undefined;
  return Object.fromEntries(entries) as JsonObject;
};

export class AudioRecordingError extends BaseError {
  public readonly source: AudioRecordingErrorSource;
  public readonly stage?: AudioRecordingErrorStage;
  public readonly summary?: string;
  public readonly details?: JsonObject;

  constructor({
    source,
    stage,
    summary,
    details,
    message,
    originalError,
    info,
    ...rest
  }: AudioRecordingErrorOptions) {
    const label = buildLabel(source, stage);
    const baseMessage =
      message ??
      (originalError instanceof Error ? originalError.message : undefined) ??
      DEFAULT_MESSAGE;
    const trimmed = baseMessage.trim();
    const prefixed = trimmed.startsWith(`[${label}]`) ? trimmed : `[${label}] ${trimmed}`;
    const computedSummary = summary ?? buildSummary(details);
    const normalizedDetails = normalizeDetails(details);
    const finalMessage = computedSummary ? `${prefixed} (${computedSummary})` : prefixed;

    const mergedInfo: JsonObject = {
      ...(info ?? {}),
      source,
      ...(stage ? { stage } : {}),
      ...(normalizedDetails ? { details: normalizedDetails } : {}),
      ...(computedSummary ? { summary: computedSummary } : {}),
    };

    super({
      ...rest,
      message: finalMessage,
      originalError,
      info: mergedInfo,
    });

    this.source = source;
    this.stage = stage;
    this.summary = computedSummary;
    this.details = mergedInfo.details as JsonObject | undefined;
  }
}
